import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Download } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { monthRange, downloadCsv, toastExportNeedsLoad } from '../reportUtils';
import { useTenantCompanyName, ReportToolbar, ReportDocument, fmtRp } from '../ReportShell';
import { COA_TYPE_LABELS } from '../accountingUiConstants';

export default function ReportLedger() {
  const company = useTenantCompanyName();
  const { start: mStart, end: mEnd } = monthRange();
  const [coa, setCoa] = useState([]);
  const [ledgerAcc, setLedgerAcc] = useState('');
  const [lgStart, setLgStart] = useState(mStart);
  const [lgEnd, setLgEnd] = useState(mEnd);
  const [lg, setLg] = useState(null);

  useEffect(() => {
    setLg(null);
  }, [ledgerAcc, lgStart, lgEnd]);

  const loadCoa = useCallback(() => {
    api.get('/tenant/accounting/coa?active_only=true').then((r) => setCoa(r.data.accounts || [])).catch(() => {});
  }, []);

  useEffect(() => {
    loadCoa();
  }, [loadCoa]);

  const loadLg = () => {
    if (!ledgerAcc) return toast.error('Pilih akun');
    api.get(`/tenant/accounting/ledger/${ledgerAcc}`, { params: { start: lgStart, end: lgEnd } }).then((r) => setLg(r.data)).catch(() => toast.error('Gagal buku besar'));
  };

  const acc = lg?.account;

  return (
    <div>
      <ReportToolbar>
        <div className="flex flex-col gap-1">
          <Label className="text-xs font-semibold text-slate-600">Akun</Label>
          <select className="h-9 min-w-[220px] rounded-md border border-input bg-background px-2 text-sm" value={ledgerAcc} onChange={(e) => setLedgerAcc(e.target.value)}>
            <option value="">— Pilih akun —</option>
            {coa.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code} — {a.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs font-semibold text-slate-600">Dari</Label>
          <Input type="date" value={lgStart} onChange={(e) => setLgStart(e.target.value)} className="h-9 w-[140px]" />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs font-semibold text-slate-600">Sampai</Label>
          <Input type="date" value={lgEnd} onChange={(e) => setLgEnd(e.target.value)} className="h-9 w-[140px]" />
        </div>
        <Button type="button" onClick={loadLg} className="h-9">
          Muat data
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-9"
          onClick={() => {
            if (!ledgerAcc) return toast.error('Pilih akun');
            if (!lg) return toastExportNeedsLoad();
            downloadCsv(`/tenant/accounting/ledger/${ledgerAcc}`, { start: lgStart, end: lgEnd }, 'buku_besar.csv');
          }}
        >
          <Download className="mr-1 h-4 w-4" />
          CSV
        </Button>
      </ReportToolbar>

      {lg?.lines?.length ? (
        <ReportDocument
          companyName={company}
          title="BUKU BESAR (GENERAL LEDGER)"
          subtitle={acc ? `${acc.code} — ${acc.name}` : 'Detail mutasi per akun'}
          periodLabel={`Periode ${lgStart} s.d. ${lgEnd}`}
        >
          {acc && (
            <div className="acct-rpt-ledger-meta">
              <strong>Tipe akun:</strong> {COA_TYPE_LABELS[acc.type] || acc.type} · <strong>Kode:</strong> {acc.code}
            </div>
          )}
          <table className="acct-rpt-table">
            <thead>
              <tr>
                <th style={{ width: '7rem' }}>Tanggal</th>
                <th style={{ width: '3.5rem' }}>No.</th>
                <th>Memo / referensi</th>
                <th className="acct-rpt-th-num">Debit</th>
                <th className="acct-rpt-th-num">Kredit</th>
                <th className="acct-rpt-th-num">Saldo berjalan</th>
              </tr>
            </thead>
            <tbody>
              {lg.lines.map((r, i) => (
                <tr key={i}>
                  <td className="font-mono text-xs whitespace-nowrap">{r.date}</td>
                  <td className="acct-rpt-num text-sm">{r.entry_number}</td>
                  <td className="max-w-md">{r.memo}</td>
                  <td className="acct-rpt-num">{r.debit ? fmtRp(r.debit) : '—'}</td>
                  <td className="acct-rpt-num">{r.credit ? fmtRp(r.credit) : '—'}</td>
                  <td className="acct-rpt-num font-semibold">{fmtRp(r.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ReportDocument>
      ) : null}

      {lg && !lg.lines?.length && <div className="acct-rpt-empty">Tidak ada mutasi untuk akun ini pada periode yang dipilih.</div>}

      {!lg && <div className="acct-rpt-empty">Pilih akun dan periode, lalu klik <strong>Muat data</strong>.</div>}
    </div>
  );
}
